import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence } from 'motion/react';
import { nanoid } from 'nanoid';
import { usePollForUpdates } from '@frontend/automatic-updates';
import envs from '@frontend/env';
import { ErrorBoundary } from '@frontend/error-boundary';
import {
  Notification,
  NotificationComponent,
  NewNotificationsProvider,
  ExpandedViewProvider,
} from '@frontend/notifications';
import { CallPopInterface, CallPopWidget } from '@frontend/pop';
import { IPCEventName, IPCRendererCallback, useIpcShellProvider, shell } from '@frontend/shell-utils';
import { WeavePopNotification } from '@frontend/types';
import { theme } from '@frontend/theme';
import { CaretLeftIconSmall, CaretRightIconSmall, IconButton, Text } from '@frontend/design-system';
import { useDebugMode } from './providers/debug-provider';
import { useNotificationManager } from './providers/notification-manager';
import { sentry } from './sentry';
import { useNotificationsStore } from './store';

sentry.initialize();

//This makes sure we have backwards compatibility until the user has updated to the latest version of the app
const popsEdgeOpenFeature = window.shell?.featureAvailability?.has?.('pops-edge-open');

const minResizeHeight = 120;
const newResizeBuffer = 16;

/**
 * This is a dirty hack to fix the window in situations where strange electron behavior causes the rendered components
 * to be invisible. Hiding and showing the window seems to fix the issue. Don't ask why. Some secrets electron guards
 * viciously.
 */
function forceWindowFix() {
  shell.emit?.('windows:notifications:hide', undefined);
  shell.emit?.('windows:notifications:show', undefined);
}

export const App = () => {
  const {
    notifications,
    addNotification,
    removeNotification,
    removeAllNotifications,
    updateNotification,
    currNotificationIndex,
    setCurrNotificationIndex,

    addPop,
    removePop,
    setPops,
    setCurrPopIndex,
    setPopOutlet,
    popOutlet,
    pops,
  } = useNotificationManager();

  //TEMP: TODO: we don't need these as refs, but keeping them as refs for now to avoid unnecessary resubscriptions/unsubscriptions whenever they change
  const notificationsRef = useRef(notifications);
  const popsRef = useRef(pops);
  notificationsRef.current = notifications;
  popsRef.current = pops;

  const preExpandedYPosition = useRef({
    isExpanded: false,
    yPosition: window.screenY,
  });
  const allowUpdates = pops.length === 0 && notifications.length === 0;
  const container = useRef<HTMLDivElement>(null);
  useAutomaticUpdates(allowUpdates);
  const notificationWindowPlacement = useNotificationsStore((state) => state.placement);

  const { debugModeEnabled, disableDebugMode } = useDebugMode();
  const hideWindow = useHideWindow();

  useHashChangeListener((hash) => {
    console.info('hash change disabled for now', hash);
  }, []);

  useNotificationShowListener((notification) => {
    console.info('Received Notification Show Event', notification);

    //  This is left here so that you can trigger a "Ghost Notification" like
    //  event by running the following in the devtools console:
    //  shell.emit('notification:show', {});
    //  This keep this from adding the resultant undefined notification to state
    if (!notification) {
      return;
    }

    if (notificationsRef.current.length === 0 && (popsRef.current.length === 0 || popOutlet !== 'queue')) {
      forceWindowFix();
    }

    addNotification(notification);
  }, []);

  useNotificationState();

  useNotificationUpdateListener((notification) => {
    console.info('Received Notification Update Event', notification);
    updateNotification(notification);
  }, []);

  useNotificationHideListener((id) => {
    console.info('Received Notification Hide Event', id);
    removeNotification(id);
  }, []);

  useNotificationClearAllListener(() => {
    removeAllNotifications();
    setPops([]);
    setPopOutlet('queue');
  }, [removeAllNotifications, setPops, setPopOutlet]);

  useEffect(() => {
    if (!shell.isShell) {
      return;
    }

    /**
     * This callback is used to sync the state of this app with data coming from the main app.
     *
     * Do not use this to emit events to the main app.
     */
    const callback: IPCRendererCallback<IPCEventName.PopAside> = (_e, data) => {
      console.log('Received Pop Aside Event', data);

      if (data.type === 'addNotification') {
        addPop(data.notification);
        if (notifications.length === 0 && (pops.length === 0 || popOutlet !== 'queue')) {
          forceWindowFix();
        }
      }
      if (data.type === 'setNotifications') {
        setPops(data.notifications);
        if (notifications.length === 0 && (pops.length === 0 || popOutlet !== 'queue')) {
          forceWindowFix();
        }
      }

      if (data.type === 'removeNotification') {
        removePop(data.id);
      }

      if (data.type === 'setOutlet') {
        setPopOutlet(data.outlet);
      }

      if (data.type === 'navigate') {
        setCurrPopIndex(data.index);
      }

      if (data.type === 'dismiss') {
        CallPopInterface._dismiss(data.id);
      }
    };

    const id = 'pop-channel' + Math.random().toString();
    shell.on?.(IPCEventName.PopAside, callback, id);
    return () => {
      shell.off?.(IPCEventName.PopAside, callback, id);
    };
  }, [pops, notifications, popOutlet]);

  useEffect(() => {
    const isEmpty = notificationsRef.current.length === 0 && (popOutlet !== 'queue' || popsRef.current.length === 0);
    if (isEmpty) {
      hideWindow();
      return;
    }
    if (notificationsRef.current.length === 1) {
      return setCurrNotificationIndex(0);
    }
    if (notificationsRef.current.length && currNotificationIndex > notificationsRef.current.length - 1) {
      return setCurrNotificationIndex(Math.max(0, notificationsRef.current.length - 1));
    }

    // in case somehow the ipc hide event failed, this will make sure the window doesn't stick around
    const interval = setInterval(() => {
      const windowIsVisible = window.document.visibilityState === 'visible';
      const isStillEmpty =
        notificationsRef.current.length === 0 && (popOutlet !== 'queue' || popsRef.current.length === 0);

      if (isStillEmpty && windowIsVisible) {
        clearInterval(interval);
        sentry.error({
          error: "Backup window hide invoked. IPC hide didn't work. Refreshing window",
          topic: 'notifications',
          team: 'fe-ux-platform',
          severityLevel: 'error',
        });
        return hideWindow(true);
      }
    }, 2000);
    return () => {
      console.log('Clearing hide interval');
      clearInterval(interval);
    };
  }, [debugModeEnabled, currNotificationIndex, notifications, pops, popOutlet]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      entries.forEach((_entry) => {
        const documentHeight = entries[0].target.clientHeight;
        const y =
          !popsEdgeOpenFeature && !preExpandedYPosition.current.isExpanded
            ? preExpandedYPosition.current.yPosition
            : undefined;

        shell?.emit?.('notification:update-options', {
          height: Math.max(minResizeHeight, documentHeight + newResizeBuffer),
          width: 404,
          hasHeightBoundConstraints: true,
          hasWidthBoundConstraints: true,
          placement: notificationWindowPlacement,
          y,
          movable: false,
        });
      });
    });

    const currentElement = container.current;

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [notificationWindowPlacement]);

  function handleGhostClick(_event: React.MouseEvent) {
    if (notifications.length > 0 || pops.length > 0) return;

    // if the window is empty, we want to hide it. It should have been hidden
    // already.
    hideWindow();
    sentry.error({
      error: 'Ghost Click Detected',
      topic: 'phone',
      team: 'frontend-ops',
      severityLevel: 'error',
    });
    console.error('Ghost Click Detected');
    // reload the app to reset the state incase it is responsible for the
    // failure to hide
    window.location.reload();
  }

  return (
    <div
      onClick={handleGhostClick}
      css={[
        css`
          transition: none !important;
          display: inline-block;
          width: 100%;
          height: 100%;
          padding-top: 8px;
          ${debugModeEnabled && `background: white; border: 2px solid black;`}
        `,
      ]}
    >
      <div
        ref={container}
        css={css`
          transition: none !important;
          width: auto;
        `}
      >
        {debugModeEnabled && (
          <div
            css={css`
              padding: 4px;
              background: #f2f2f2;
            `}
          >
            Debug Mode On <button onClick={disableDebugMode}>Turn Off</button>
          </div>
        )}

        {popOutlet === 'queue' && pops.length > 0 && <CallPopWidget notifications={pops} />}
        <NewNotificationsProvider value={true}>
          <div style={{ width: 360 }}>
            <NewNotificationsExperience preExpandedYPosition={preExpandedYPosition} />
          </div>
        </NewNotificationsProvider>

        {/* <pre>{notifications ? JSON.stringify(notifications, undefined, 2) : 'No Notification Set'}</pre> */}
      </div>
    </div>
  );
};

type DefaultNotificationsProps = {
  currNotificationIndex: number;
  notifications: WeavePopNotification[];
  setCurrNotificationIndex: (i: number) => void;
  removeNotification: (id: string) => void;
  currNotification?: WeavePopNotification;
};

// Export the Fallback component for testing
export const DefaultNotificationsFallback = (props: {
  currNotification?: WeavePopNotification;
  removeNotification: (id: string) => void;
  resetError: () => void;
}) => {
  useEffect(() => {
    if (props.currNotification?.id) {
      props.removeNotification(props.currNotification.id);
      props.resetError();
    }
  }, [props.currNotification?.id, props.removeNotification, props.resetError]);

  return <>{/* Nothing to show - should auto-dismiss */}</>;
};

// Export the component for testing
export const DefaultNotifications = ({
  currNotificationIndex,
  notifications,
  setCurrNotificationIndex,
  removeNotification,
  currNotification,
}: DefaultNotificationsProps) => {
  const hasSlider = notifications.length > 1;

  const Fallback = (fallbackProps: { resetError: () => void }) => {
    return (
      <DefaultNotificationsFallback
        currNotification={currNotification}
        removeNotification={removeNotification}
        resetError={fallbackProps.resetError}
      />
    );
  };

  return (
    <div
      css={css`
        border-radius: ${hasSlider ? theme.borderRadius.medium : 0};
        box-shadow: ${hasSlider ? theme.shadows.heavy : 'none'};
        display: block;
      `}
    >
      {hasSlider && (
        <Slider currIndex={currNotificationIndex} total={notifications.length} onChange={setCurrNotificationIndex} />
      )}
      {currNotification && (
        <ErrorBoundary
          tier='feature'
          feature='notification.default-notifications'
          team='frontend-ops'
          fallback={Fallback}
        >
          <NotificationComponent
            stacked={hasSlider}
            key={currNotification.id}
            notification={currNotification}
            emit={(action, notification) => {
              if (action.action === 'timed-out') {
                removeNotification(notification.id);
              }

              shell.emit?.('notification:action', {
                action,
                notification,
                //TODO: Somehow it's not associating that the notification type corresponds to the right one.
                // In reality, it does, but I'm struggling to tell typescript that it does. Please send help.
              } as any);
            }}
          />
        </ErrorBoundary>
      )}
    </div>
  );
};

const NewNotificationsExperience = ({
  preExpandedYPosition,
}: {
  preExpandedYPosition: React.MutableRefObject<{
    isExpanded: boolean;
    yPosition?: number;
  }>;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { notifications, removeNotification, removeAllNotifications } = useNotificationManager();

  const sendPendoNotification = (
    action: 'clear-all' | 'expand' | 'collapse' | 'dismiss' | 'view' | 'respond',
    notificationType?: WeavePopNotification['type']
  ) => {
    if (shell.isShell && shell?.invoke) {
      shell
        .invoke('notifications:pendo', {
          action,
          notificationType,
        })
        .catch((err) => {
          console.error('Failed to send Pendo notification', err);
        });
    }
  };

  const handleNotificationsRemoval = useCallback(() => {
    removeAllNotifications();

    sendPendoNotification('clear-all');
  }, [removeAllNotifications]);

  useEffect(() => {
    if (popsEdgeOpenFeature) return;

    if (notifications.length <= 1) {
      setIsExpanded(false);
      preExpandedYPosition.current = {
        isExpanded: false,
        yPosition: undefined,
      };
      return;
    }
  }, [notifications.length]);

  const hasPreference = notifications.some((item) => item.type === 'preference');

  useEffect(() => {
    if (popsEdgeOpenFeature) return;

    if (isExpanded) {
      // Store the Y position just before expanding
      preExpandedYPosition.current = {
        isExpanded: true,
        yPosition: window.screenY,
      };
    } else {
      preExpandedYPosition.current = {
        isExpanded: false,
        yPosition: preExpandedYPosition.current.yPosition,
      };
    }
  }, [isExpanded, preExpandedYPosition]);

  return (
    <ExpandedViewProvider value={isExpanded}>
      <div
        style={{
          display: 'flex',
          zIndex: theme.zIndex.alerts,
          flexDirection: 'column',
          gap: theme.spacing(1),
          padding: theme.spacing(1),
          height: '100%',
          paddingBottom: theme.spacing(4),
          backdropFilter: 'blur(10px)',
        }}
        css={css`
          ::before {
            content: '';
            position: absolute;
            top: ${theme.spacing(-1)};
            left: 0;
            width: 100%;
            height: calc(100% - 16px);
            mask-image: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, black 40%, black 100%);
            z-index: -1;
            opacity: ${isExpanded ? 1 : 0};
            transition: opacity 0.2s ease-in-out;
          }
        `}
      >
        {notifications.length > 1 && (
          <Notification.OpenNotificationsNew
            isExpanded={isExpanded}
            onExpand={() => {
              setIsExpanded((prev) => {
                sendPendoNotification(prev ? 'collapse' : 'expand');

                return !prev;
              });
            }}
            disableClearAll={hasPreference}
            onClearAll={handleNotificationsRemoval}
            total={notifications.length}
            queue={notifications}
          />
        )}
        {!isExpanded ? (
          notifications.length > 0 ? (
            <Notification
              isPaused={isExpanded}
              key={notifications[0].id}
              notification={notifications[0]}
              onEmit={(action, notification) => {
                if (action === 'timed-out') {
                  removeNotification(notification.id);
                }

                if ((action as any)?.action === 'view') {
                  sendPendoNotification('view', notification?.type);
                }

                if ((action as any)?.action === 'respond') {
                  sendPendoNotification('respond', notification?.type);
                }

                if ((action as any)?.action === 'ignore') {
                  sendPendoNotification('dismiss', notification?.type);
                }

                shell.emit?.('notification:action', { action, notification } as any);
              }}
              containerProps={{ id: notifications[0].id, xAlignment: 'center' }}
              containerStyles={css`
                border-radius: ${theme.borderRadius.medium};
                box-shadow: ${theme.shadows.heavy};
                position: relative;
                width: 100%;
              `}
            >
              {!isExpanded && <Notification.ProgressBarNew />}
              <Notification.Content stacked={notifications.length > 1} />
            </Notification>
          ) : null
        ) : (
          notifications.map((notificationInView) => (
            <Notification
              isPaused={isExpanded}
              key={notificationInView.id}
              notification={notificationInView}
              onEmit={(action, notification) => {
                if (action === 'timed-out') {
                  removeNotification(notification.id);
                }

                if ((action as any)?.action === 'view') {
                  sendPendoNotification('view', notification?.type);
                }

                if ((action as any)?.action === 'respond') {
                  sendPendoNotification('respond', notification?.type);
                }

                if ((action as any)?.action === 'ignore') {
                  sendPendoNotification('dismiss', notification?.type);
                }

                shell.emit?.('notification:action', { action, notification } as any);
              }}
              containerProps={{ id: notificationInView.id, xAlignment: 'center' }}
              containerStyles={css`
                border-radius: ${theme.borderRadius.medium};
                box-shadow: ${theme.shadows.heavy};
                position: relative;
                width: 100%;
              `}
            >
              {!isExpanded && <Notification.ProgressBarNew />}
              <Notification.Content stacked={notifications.length > 1} />
            </Notification>
          ))
        )}
      </div>
    </ExpandedViewProvider>
  );
};

interface ExperimentalNotificationsProps {
  currNotification?: WeavePopNotification;
  notifications: WeavePopNotification[];
  currNotificationIndex: number;
  setCurrNotificationIndex: (index: number) => void;
  removeNotification: (id: string) => void;
  removeAllNotifications: () => void;
}

// Export the ExperimentalNotifications Fallback for testing
export const ExperimentalNotificationsFallback = (props: {
  currNotification?: WeavePopNotification;
  removeNotification: (id: string) => void;
  resetError: () => void;
}) => {
  useEffect(() => {
    if (props.currNotification) {
      props.removeNotification(props.currNotification.id);
      props.resetError();
    }
  }, [props.currNotification, props.removeNotification, props.resetError]);

  return <>{/* Nothing to show - should auto-dismiss */}</>;
};

// Export the component for testing
export const ExperimentalNotifications = ({
  currNotification,
  notifications,
  currNotificationIndex,
  setCurrNotificationIndex,
  removeNotification,
  removeAllNotifications,
}: ExperimentalNotificationsProps) => {
  if (!currNotification) return null;
  const hasPreference = notifications.some((n) => n.type === 'preference');

  const Fallback = (fallbackProps: { resetError: () => void }) => {
    return (
      <ExperimentalNotificationsFallback
        currNotification={currNotification}
        removeNotification={removeNotification}
        resetError={fallbackProps.resetError}
      />
    );
  };

  return (
    <ErrorBoundary
      tier='feature'
      feature='notification.experimental-notifications'
      team='frontend-ops'
      fallback={Fallback}
    >
      <Notification
        notification={currNotification}
        onEmit={(action, notification) => {
          if (action === 'timed-out') {
            removeNotification(notification.id);
          }
          shell.emit?.('notification:action', { action, notification } as any);
        }}
      >
        <Notification.ProgressBar />
        <AnimatePresence>
          {notifications.length > 1 && (
            <Notification.Slider
              currIndex={currNotificationIndex}
              total={notifications.length}
              onChange={setCurrNotificationIndex}
              queue={notifications}
              onClearAll={removeAllNotifications}
              disableClearAll={hasPreference}
            />
          )}
        </AnimatePresence>
        <Notification.Content stacked={notifications.length > 1} />
      </Notification>
    </ErrorBoundary>
  );
};

const useNotificationShowListener = (
  onShowNotification?: (notification: WeavePopNotification) => void,
  deps?: any[]
) => {
  useEffect(() => {
    if (!shell.isShell) {
      return;
    }
    const id = 'notification:show' + Math.random();
    const callback: IPCRendererCallback<'notification:show'> = (_e, data) => {
      onShowNotification?.(data.notification);
    };
    shell.on?.('notification:show', callback, id);
    return () => {
      if (callback && id) {
        shell.off?.('notification:show', callback, id);
      }
    };
  }, deps ?? []);
};

const useNotificationState = () => {
  const callback: IPCRendererCallback<'notification:state'> = useCallback((_e, data) => {
    useNotificationsStore.setState({
      placement: data.placement,
    });
  }, []);
  useEffect(() => {
    const id = nanoid();
    shell.on?.('notification:state', callback, id);
    return () => {
      shell.off?.('notification:state', callback, id);
    };
  }, []);
};

const useNotificationUpdateListener = (onUpdate?: (notification: WeavePopNotification) => void, deps?: any[]) => {
  useEffect(() => {
    if (!shell.isShell || !shell.featureAvailability?.has?.('clear-notifications')) {
      return;
    }
    const id = 'notification:update' + Math.random();
    const callback: IPCRendererCallback<'notification:update'> = (_e, data) => {
      onUpdate?.(data.notification);
    };
    shell.on?.('notification:update', callback, id);
    return () => {
      if (callback && id) {
        shell.off?.('notification:update', callback, id);
      }
    };
  }, deps ?? []);
};

const useNotificationHideListener = (onHideNotification?: (id: string) => void, deps?: any[]) => {
  const { status } = useIpcShellProvider();
  useEffect(() => {
    if (!shell?.isShell) {
      return;
    }

    const id = 'notification:hide' + Math.random();
    const callback: IPCRendererCallback<'notification:hide'> = (_e, data) => {
      onHideNotification?.(data);
    };
    shell.on?.('notification:hide', callback, id);
    return () => {
      if (callback && id) {
        shell.off?.('notification:hide', callback, id);
      }
    };
  }, [...(deps ?? []), status]);
};

const useNotificationClearAllListener = (onClearAll?: () => void, deps?: any[]) => {
  useEffect(() => {
    if (!shell.isShell) {
      return;
    }

    const id = 'notifications:clear-all' + Math.random();
    const callback: IPCRendererCallback<'notifications:clear-all'> = () => {
      onClearAll?.();
    };
    shell.on?.(IPCEventName.ClearNotifications, callback, id);
    return () => {
      shell.off?.(IPCEventName.ClearNotifications, callback, id);
    };
  }, deps ?? []);
};

const useHashChangeListener = (onHashChange?: (hash: any) => void, deps?: any[]) => {
  const getHashPayload = () => {
    const hash = window.location.hash;
    if (!window.location.hash) {
      return undefined;
    }
    try {
      return JSON.parse(decodeURIComponent(window.location.hash.substring(1)));
    } catch {
      console.warn('Invalid Hash', hash);
      return undefined;
    }
  };

  useEffect(() => {
    addEventListener('hashchange', (_event) => {
      const hash = getHashPayload();
      onHashChange?.(hash);
    });
  }, deps ?? []);
};

const useHideWindow = () => {
  const { debugModeEnabled } = useDebugMode();
  /**
   *
   * @param refreshWindow is useful for cases you want to be more aggressive with the window closing. In case the IPC connection is severed, this will still close the window
   */
  return (refreshWindow = false) => {
    if (!debugModeEnabled) {
      shell.emit?.('notifications:hide', undefined);
      if (refreshWindow) {
        window.location.reload();
      }
    }
  };
};

type SliderProps = {
  currIndex: number;
  total: number;
  onChange: (i: number) => void;
};
const Slider = (props: SliderProps) => {
  return (
    <div
      css={css`
        display: flex;
        background: ${theme.colors.neutral10};
        padding: ${theme.spacing(0.5)};
        justify-content: flex-end;
        align-items: center;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        border-bottom: 1px solid ${theme.colors.neutral20};
      `}
    >
      <IconButton
        disabled={props.currIndex <= 0}
        size='small'
        label='Previous'
        css={css`
          display: inline-flex;
        `}
        onClick={() => props.onChange(props.currIndex - 1)}
      >
        <CaretLeftIconSmall />
      </IconButton>

      <Text as={'span'}>
        {props.currIndex + 1} / {props.total}
      </Text>
      <IconButton
        disabled={props.currIndex >= props.total - 1}
        size='small'
        label='Next'
        css={css`
          display: inline-flex;
        `}
        onClick={() => props.onChange(props.currIndex + 1)}
      >
        <CaretRightIconSmall />
      </IconButton>
    </div>
  );
};

const useAutomaticUpdates = (allowUpdates: boolean) => {
  const url = envs.PUBLISH_URL ? 'https://' + envs.PUBLISH_URL + '/_m/version' : undefined;

  const { updateAvailable, currentVersion } = usePollForUpdates(
    10 * 60 * 1000, // 10 minutes
    url
  );

  useEffect(() => {
    if (updateAvailable && allowUpdates) {
      console.log('Refreshing notification renderer because new version is available (' + currentVersion + ')');
      window.location.reload();
    }
  }, [updateAvailable, allowUpdates]);
};
